import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import type { ApiReportData } from '../resident-report-detail/map-report-detail';

type ApiSuccessResponse<T> = {
  status: 'success';
  msg: T;
};

type ApiErrorResponse = {
  status: 'error';
  msg: string;
};

type SaveReportResponse = {
  message?: string;
  updated?: boolean;
  data?: {
    id?: number;
    pdfUrl?: string | null;
  };
};

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Сервер вернул некорректный ответ');
  }
}

function assertSuccessResponse<T>(
  response: Response,
  result: ApiSuccessResponse<T> | ApiErrorResponse,
  fallbackMessage: string,
): T {
  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : fallbackMessage,
    );
  }

  return result.msg;
}

export async function fetchReportList(): Promise<unknown[]> {
  const response = await fetch(getApiUrl('/api/personal/report/'));
  const result = await parseJsonResponse<ApiSuccessResponse<unknown[]> | ApiErrorResponse>(response);
  const data = assertSuccessResponse(response, result, 'Не удалось загрузить список отчётов');

  return Array.isArray(data) ? data : [];
}

export async function fetchReportDetail(reportId: number): Promise<ApiReportData> {
  const response = await fetch(getApiUrl(`/api/personal/report/${reportId}/`));
  const result = await parseJsonResponse<ApiSuccessResponse<ApiReportData> | ApiErrorResponse>(
    response,
  );
  return assertSuccessResponse(response, result, 'Не удалось загрузить отчёт');
}

export function getReportArchiveUrl(reportId: number, allFile = true): string {
  const url = new URL(getApiUrl(`/api/personal/report/${reportId}/archive/`), window.location.origin);
  if (allFile) {
    url.searchParams.set('allFile', '1');
  }
  return url.toString();
}

export function getReportSaveUrl(reportId?: number | null): string {
  const id = reportId && reportId > 0 ? reportId : 0;
  return getApiUrl(`/api/personal/report/${id}/`);
}

export function getReportSignUrl(reportId: number): string {
  return getApiUrl(`/api/personal/report/${reportId}/sign/`);
}

export async function saveReport(formData: FormData, reportId?: number | null): Promise<SaveReportResponse> {
  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  formData.set('sessid', sessid);

  const response = await fetch(getReportSaveUrl(reportId), {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
  });

  const result = await parseJsonResponse<ApiSuccessResponse<SaveReportResponse> | ApiErrorResponse>(
    response,
  );
  return assertSuccessResponse(response, result, 'Не удалось сохранить отчёт');
}

export async function deleteReport(reportId: number): Promise<string> {
  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const response = await fetch(getApiUrl(`/api/personal/report/${reportId}/`), {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
  });

  const result = await parseJsonResponse<ApiSuccessResponse<string> | ApiErrorResponse>(response);
  return assertSuccessResponse(response, result, 'Не удалось удалить отчёт');
}

export async function signReport(
  reportId: number,
  payload: { content: string; signature: string },
): Promise<void> {
  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const body = new FormData();
  body.append('id', String(reportId));
  body.append('content', payload.content);
  body.append('signature', payload.signature);
  body.append('sessid', sessid);

  const response = await fetch(getReportSignUrl(reportId), {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
  });

  const result = await parseJsonResponse<ApiSuccessResponse<string> | ApiErrorResponse>(response);
  assertSuccessResponse(response, result, 'Не удалось подписать отчёт');
}

export async function fetchPdfAsBase64(pdfUrl: string): Promise<string> {
  const response = await fetch(pdfUrl, { method: 'GET', credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Не удалось загрузить PDF (${response.status})`);
  }

  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return btoa(binary);
}
